import { XIcon, type LucideIcon } from "lucide-react";
import { motion } from "motion/react";
import type { ReactNode } from "react";

import { HeroBackdrop, HeroChip, containerVariants, itemVariants } from "./shell-shared";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/**
 * Standard scaffolding for form and content dialogs. The shell owns the header
 * (icon tile + title + description) and an optional footer slot; the feature
 * keeps full control of the content in between.
 *
 * Drop-in compatible with the layout pack's DialogShell (same path, same
 * props). Open Calendar renders the header as a compact card band in the
 * landing's "lime planner" look: lime chip, ring texture, extrabold type, light
 * or dark with the theme.
 *
 * The default Dialog close control is replaced here: the header's z-index layer
 * would sit over the absolute close button and swallow clicks.
 */
export interface DialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  /** Footer slot rendered below the content (actions, hints, etc.). */
  footer?: ReactNode;
  /** Optional `<DialogTrigger asChild>` element rendered inside the Dialog root. */
  trigger?: ReactNode;
  /** Dialog body. Rendered unchanged between the header and the footer. */
  children: ReactNode;
  className?: string;
}

export const DialogShell = ({
  open,
  onOpenChange,
  icon: Icon,
  title,
  description,
  footer,
  trigger,
  children,
  className,
}: DialogShellProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger}
      <DialogContent
        showCloseButton={false}
        className={cn(
          // Darker edge so the ink header doesn't sit inside a pale hairline frame. `overflow-clip`, not
          // `-hidden`: a hidden box still scrolls when focus or a caret asks to be shown, which slid the
          // whole dialog up under its header and left a blank band below the footer.
          "flex max-h-[calc(100vh-2rem)] flex-col gap-0 overflow-clip rounded-[calc(var(--radius)+10px)] border-border p-0",
          className,
        )}
      >
        <div className="relative shrink-0 overflow-clip border-b border-border/70 bg-card text-foreground">
          <HeroBackdrop />
          {/* A lone title centres on the chip; with a description the pair hangs from its top. */}
          <div
            className={cn(
              "relative z-10 flex gap-4 px-6 pb-5 pt-6 pr-14",
              description ? "items-start" : "items-center",
            )}
          >
            <HeroChip icon={Icon} />
            <div className="space-y-1">
              <DialogTitle className="text-lg font-extrabold tracking-[-0.02em] text-foreground">
                {title}
              </DialogTitle>
              {description ? (
                <DialogDescription asChild>
                  <div className="text-sm font-medium leading-[1.6] text-foreground/70">
                    {description}
                  </div>
                </DialogDescription>
              ) : null}
            </div>
          </div>
          <DialogClose
            className={cn(
              "absolute top-4 right-4 z-20 border-0 bg-transparent p-1 shadow-none transition-opacity",
              "text-foreground/70 opacity-70 hover:bg-transparent hover:text-foreground hover:opacity-100",
              "focus:outline-hidden focus:ring-0 focus-visible:ring-0",
              "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
            )}
          >
            <XIcon />
            <span className="sr-only">{m.layout_dialog_close()}</span>
          </DialogClose>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="flex min-h-0 flex-col overflow-hidden"
        >
          <motion.div
            variants={itemVariants}
            // `relative` keeps visually hidden inputs (absolute) inside the scrolling body, not the box.
            className={cn("relative min-h-0 overflow-y-auto px-6 pt-5", footer ? "pb-1" : "pb-5")}
          >
            {children}
          </motion.div>

          {footer ? (
            <motion.div variants={itemVariants} className="shrink-0 border-t border-border/50 px-6 pb-5 pt-4">
              {footer}
            </motion.div>
          ) : null}
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};
